// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getProxCardMaxNumber, updateProxCardMaxNumber } from '@api/portalServices';
import type { ProxCardMaxNumberGetResponse, ProxCardMaxNumberUpdateRequest } from '@api/types';
import type { ApiError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';

/**
 * Custom hook to fetch the proximity card maximum number configuration.
 *
 * Background refetch triggers default to React Query's enabled behaviour; callers
 * can suspend them (e.g. while a form has unsaved edits) to avoid clobbering
 * in-progress input with a stale server value.
 */
export const useProxCardMaxNumberQuery = (options?: {
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
}) => {
  return useQuery<ProxCardMaxNumberGetResponse, ApiError>({
    queryKey: [QueryKey.PROX_CARD_MAX_NUMBER],
    queryFn: getProxCardMaxNumber,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
    refetchOnReconnect: options?.refetchOnReconnect ?? true,
  });
};

/**
 * Custom hook to update the proximity card maximum number configuration
 */
export const useProxCardMaxNumberMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, ProxCardMaxNumberUpdateRequest>({
    mutationFn: async (data: ProxCardMaxNumberUpdateRequest) => {
      await updateProxCardMaxNumber(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROX_CARD_MAX_NUMBER] });
    },
    retry: false,
  });
};
